import { useEffect, useState } from "react";
import { Plus, ClipboardCheck, Gift, Package, Clock, Trash2, User, AlertCircle } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export default function RedemptionsPage() {
  return (
    <DashboardShell active="redemptions">
      {() => <RedemptionsBody />}
    </DashboardShell>
  );
}

const emptyForm = {
  offer_id: "",
  customer_name: "",
  customer_note: "",
  employee_name: "",
  channel: "radar",
};

function RedemptionsBody() {
  const [offers, setOffers] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterOffer, setFilterOffer] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [o, s, r] = await Promise.all([
        api.get("/merchants/me/offers"),
        api.get("/merchants/me/redemptions/summary"),
        api.get("/merchants/me/redemptions", { params: filterOffer ? { offer_id: filterOffer } : {} }),
      ]);
      setOffers(o.data);
      setSummary(s.data);
      setItems(r.data);
    } catch (e) { console.warn("silent error:", e && e.message || e); }
  };

  useEffect(() => { load(); }, [filterOffer]); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const openNew = () => {
    setForm({ ...emptyForm, offer_id: offers[0]?.offer_id || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.offer_id) {
      toast.error("Selecciona una oferta");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/merchants/me/redemptions", form);
      if (data.over_quota) {
        toast.warning("Registrada, pero superaste la cuota por hora");
      } else {
        toast.success("Entrega registrada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`/merchants/me/redemptions/${r.redemption_id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { console.warn("silent error:", e && e.message || e); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Control de entregas</p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Usuarios beneficiados</h1>
          <p className="text-sm text-neutral-500 mt-1">Registra cada vez que un cliente reclama un descuento, promoción o regalo. Tus empleados pueden reportarlo aquí.</p>
        </div>
        <Button onClick={openNew} disabled={offers.length === 0} className="rounded-full bg-[#FF007A] hover:bg-[#e5006e] font-bold" data-testid="new-redemption-btn">
          <Plus className="w-4 h-4" /> Registrar entrega
        </Button>
      </div>

      {offers.length === 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-center gap-2" data-testid="no-offers-warning">
          <AlertCircle className="w-4 h-4" />
          Primero crea una oferta en la sección &quot;Ofertas&quot; para poder registrar entregas.
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard label="Hoy" value={summary?.today ?? "—"} icon={ClipboardCheck} color="text-[#FF007A]" />
        <SummaryCard label="Últimos 7 días" value={summary?.week ?? "—"} icon={Clock} color="text-[#0055FF]" />
        <SummaryCard label="Total histórico" value={summary?.total ?? "—"} icon={Gift} color="text-emerald-600" />
        <SummaryCard label="Ofertas activas" value={offers.filter((o) => o.active).length} icon={Package} color="text-amber-500" />
      </div>

      {/* Per-offer quota status */}
      {summary?.per_offer?.some((p) => p.quantity_per_hour > 0) && (
        <div className="rounded-2xl bg-white border border-neutral-200 p-5">
          <h2 className="font-heading font-extrabold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Cuota por hora
          </h2>
          <div className="space-y-2">
            {summary.per_offer.filter((p) => p.quantity_per_hour > 0).map((p) => {
              const used = p.quantity_per_hour - (p.remaining_this_hour ?? 0);
              const pct = Math.min(100, (used / p.quantity_per_hour) * 100);
              return (
                <div key={p.offer_id}>
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="truncate max-w-[70%]">{p.title}</span>
                    <span className="text-neutral-500">{used}/{p.quantity_per_hour} esta hora</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Label className="text-xs uppercase tracking-wider text-neutral-500 font-bold">Filtrar por oferta</Label>
        <select
          value={filterOffer}
          onChange={(e) => setFilterOffer(e.target.value)}
          className="h-10 rounded-full border border-neutral-200 bg-white px-4 text-sm"
          data-testid="filter-offer-select"
        >
          <option value="">Todas</option>
          {offers.map((o) => (
            <option key={o.offer_id} value={o.offer_id}>{o.title}</option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-16 text-center" data-testid="empty-redemptions">
            <ClipboardCheck className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <div className="font-heading font-extrabold text-lg">Sin entregas registradas todavía</div>
            <p className="text-sm text-neutral-500 mt-1">Registra la primera vez que un cliente use un descuento o reciba un regalo.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#FAFAFA] text-xs uppercase text-neutral-500">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Fecha</th>
                <th className="text-left px-5 py-3 font-bold">Oferta</th>
                <th className="text-left px-5 py-3 font-bold hidden md:table-cell">Cliente</th>
                <th className="text-left px-5 py-3 font-bold hidden md:table-cell">Empleado</th>
                <th className="text-left px-5 py-3 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.redemption_id} className="border-t border-neutral-100" data-testid={`redemption-row-${r.redemption_id}`}>
                  <td className="px-5 py-3 text-xs text-neutral-500">
                    {new Date(r.created_at).toLocaleString("es")}
                    {r.over_quota && (
                      <div className="text-[10px] font-bold text-red-500 mt-0.5">Sobre cuota</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-sm font-bold">{r.offer_title}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#FF007A] font-bold">{r.offer_kind}</div>
                  </td>
                  <td className="px-5 py-3 text-sm hidden md:table-cell">
                    {r.customer_name || <span className="text-neutral-400">—</span>}
                    {r.customer_note && (
                      <div className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{r.customer_note}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm hidden md:table-cell">
                    <div className="flex items-center gap-1.5 text-neutral-700">
                      <User className="w-3.5 h-3.5" /> {r.employee_name || <span className="text-neutral-400">—</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => remove(r)} className="text-neutral-400 hover:text-red-500 transition-colors" data-testid={`delete-redemption-${r.redemption_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-extrabold">Registrar entrega</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Oferta entregada</Label>
              <select
                value={form.offer_id}
                onChange={(e) => setForm({ ...form, offer_id: e.target.value })}
                className="mt-1.5 w-full h-11 rounded-xl border border-neutral-200 px-3 text-sm bg-white"
                data-testid="redemption-offer-select"
              >
                {offers.map((o) => (
                  <option key={o.offer_id} value={o.offer_id}>
                    {`[${o.kind}] ${o.title}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Nombre del cliente (opcional)</Label>
              <Input
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                placeholder="María, mesa 4, etc."
                className="mt-1.5 h-11 rounded-xl"
                data-testid="redemption-customer-input"
              />
            </div>
            <div>
              <Label>Nota (opcional)</Label>
              <Textarea
                value={form.customer_note}
                onChange={(e) => setForm({ ...form, customer_note: e.target.value })}
                placeholder="Ej: Se llevó 2 muffins y un café"
                className="mt-1.5 rounded-xl"
                rows={2}
                data-testid="redemption-note-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Empleado</Label>
                <Input
                  value={form.employee_name}
                  onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                  placeholder="Tu nombre"
                  className="mt-1.5 h-11 rounded-xl"
                  data-testid="redemption-employee-input"
                />
              </div>
              <div>
                <Label>Canal</Label>
                <select
                  value={form.channel}
                  onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  className="mt-1.5 w-full h-11 rounded-xl border border-neutral-200 px-3 text-sm bg-white"
                >
                  <option value="radar">Vino por el radar</option>
                  <option value="walkin">Cliente que ya estaba</option>
                  <option value="referral">Recomendado</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-black text-white font-bold" data-testid="save-redemption-btn">
              {saving ? "Guardando..." : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-5">
      <Icon className={`w-5 h-5 ${color}`} />
      <div className="text-xs text-neutral-500 font-medium mt-3">{label}</div>
      <div className="font-heading font-extrabold text-3xl mt-1" data-testid={`redemption-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</div>
    </div>
  );
}
